import React, { memo, Fragment, useState, useEffect } from "react";
import { Row, Col, Table, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../../../../components/bootstrap/card";
import { getInstructorLists, getInstructorOvertimeLesson, updateInstructorOvertimeLesson } from "../../../../api/instructor";
import useUserMe from "../../../../hooks/useUserMe";

const InstructorOvertime = memo(() => {
  const navigate = useNavigate();
  const { data: userMeData } = useUserMe();
  const storeId = userMeData?.instructor?.storeId;
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [instructorList, setInstructorList] = useState([]);
  const [originalData, setOriginalData] = useState({}); // 서버에서 가져온 원본 데이터
  const [editingData, setEditingData] = useState({}); // 수정 중인 데이터
  const [editingState, setEditingState] = useState({}); // 각 행의 수정 모드 상태
  const [loading, setLoading] = useState(false);

  // 연도 선택 옵션
  const years = [...new Set([2026, ...Array.from({ length: 3 }, (_, i) => currentYear - i)])].sort((a, b) => b - a);

  // 강사 목록 및 오버타임 데이터 조회
  useEffect(() => {
    const fetchData = async () => {
      if (!storeId || !userMeData) return;
      setLoading(true);
      try {
        const data = await getInstructorLists({ storeId, isWork: true });
        
        // DIRECTOR가 아닌 경우, 자신의 데이터만 필터링
        const filteredData = userMeData?.instructor?.position !== 'DIRECTOR'
          ? data.filter(instructor => instructor.id === userMeData?.instructor?.id)
          : data;
        
        setInstructorList(filteredData || []);
        
        // 초기 데이터 구조 생성
        const initialOriginalData = {};
        filteredData.forEach((instructor) => {
          const monthlyData = {};
          for (let i = 1; i <= 12; i++) {
            monthlyData[i] = 0;
          }
          initialOriginalData[instructor.id] = {
            name: instructor.user?.name || '',
            monthlyData,
          };
        });
        
        // API에서 오버타임 데이터 가져오기
        try {
          const overtimeResponse = await getInstructorOvertimeLesson(storeId, selectedYear.toString());
          overtimeResponse.forEach((item) => {
            if (initialOriginalData[item.instructorId]) {
              // month1 ~ month12를 1~12의 키로 변환
              const monthlyData = {};
              for (let i = 1; i <= 12; i++) {
                monthlyData[i] = item[`month${i}`] || 0;
              }
              initialOriginalData[item.instructorId].monthlyData = monthlyData;
            }
          });
        } catch (error) {
          console.error("Failed to fetch overtime lesson data:", error);
          // API 에러가 발생해도 기본 구조는 유지
        }
        
        setOriginalData(initialOriginalData);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storeId, selectedYear, userMeData]);

  const handleBackClick = () => {
    navigate(-1);
  };

  // 수정 모드 시작
  const handleEdit = (instructorId) => {
    const original = originalData[instructorId];
    if (!original) return;
    
    setEditingData((prev) => ({
      ...prev,
      [instructorId]: {
        ...original,
        monthlyData: { ...original.monthlyData },
      },
    }));
    
    setEditingState((prev) => ({
      ...prev,
      [instructorId]: true,
    }));
  };

  // 수정 중인 값 변경
  const handleOvertimeChange = (instructorId, month, value) => {
    setEditingData((prev) => ({
      ...prev,
      [instructorId]: {
        ...prev[instructorId],
        monthlyData: {
          ...prev[instructorId]?.monthlyData,
          [month]: parseInt(value) || 0,
        },
      },
    }));
  };

  // 저장
  const handleSave = async (instructorId) => {
    const data = editingData[instructorId];
    if (!data) return;

    try {
      // API 요청 데이터 형식 변환 (monthlyData 객체를 month1~month12로 변환)
      const requestData = {
        year: selectedYear.toString(),
      };
      for (let i = 1; i <= 12; i++) {
        requestData[`month${i}`] = data.monthlyData[i] || 0;
      }
      
      await updateInstructorOvertimeLesson(instructorId, requestData);
      
      // 원본 데이터 업데이트
      setOriginalData((prev) => ({
        ...prev,
        [instructorId]: {
          ...data,
          monthlyData: { ...data.monthlyData },
        },
      }));
      
      // 수정 모드 종료
      setEditingState((prev) => ({
        ...prev,
        [instructorId]: false,
      }));
      
      // 수정 중인 데이터 제거
      setEditingData((prev) => {
        const newData = { ...prev };
        delete newData[instructorId];
        return newData;
      });
      
      alert("저장되었습니다.");
    } catch (error) {
      console.error("Failed to save overtime data:", error);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  // 취소
  const handleCancel = (instructorId) => {
    setEditingState((prev) => ({
      ...prev,
      [instructorId]: false,
    }));
    
    setEditingData((prev) => {
      const newData = { ...prev };
      delete newData[instructorId];
      return newData;
    });
  };

  return (
    <Fragment>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">계약시간 외 수업횟수</h4>
                <p className="text-muted mb-0">
                  월별 계약시간 외 수업횟수를 입력할 수 있습니다.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  style={{ width: "150px" }}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}년
                    </option>
                  ))}
                </Form.Select>
                <Button type="button" variant="btn btn-light" onClick={handleBackClick}>
                  뒤로가기
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                  <Table style={{ minWidth: "1200px", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ width: "75px", padding: "8px 2px" }}>강사명</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>1월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>2월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>3월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>4월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>5월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>6월</th>
                        <th className="text-center" style={{ width: "70px", padding: "8px 2px" }}>상반기 합계</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>7월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>8월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>9월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>10월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>11월</th>
                        <th className="text-center" style={{ width: "55px", padding: "8px 2px" }}>12월</th>
                        <th className="text-center" style={{ width: "70px", padding: "8px 2px" }}>하반기 합계</th>
                        <th className="text-center" style={{ width: "100px", padding: "8px 2px" }}>액션</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instructorList.length === 0 ? (
                        <tr>
                          <td colSpan="16" className="text-center">
                            강사 데이터가 없습니다.
                          </td>
                        </tr>
                      ) : (
                        instructorList.map((instructor) => {
                          const isEditing = editingState[instructor.id] || false;
                          const data = isEditing 
                            ? editingData[instructor.id] 
                            : (originalData[instructor.id] || {
                                name: instructor.user?.name || '',
                                monthlyData: {},
                              });
                          
                          // 월별 데이터 초기화
                          const monthly = {};
                          for (let i = 1; i <= 12; i++) {
                            monthly[i] = data.monthlyData?.[i] || 0;
                          }

                          // 상반기 합계 (1-6월)
                          const firstHalfSum = [1, 2, 3, 4, 5, 6].reduce((sum, month) => sum + (monthly[month] || 0), 0);
                          // 하반기 합계 (7-12월)
                          const secondHalfSum = [7, 8, 9, 10, 11, 12].reduce((sum, month) => sum + (monthly[month] || 0), 0);

                          return (
                            <tr key={instructor.id}>
                              <td><strong>{data.name}</strong></td>
                              {/* 상반기 (1-6월) */}
                              {[1, 2, 3, 4, 5, 6].map((month) => (
                                <td key={month} className="text-center" style={{ padding: "8px 2px" }}>
                                  {isEditing ? (
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      value={monthly[month] || 0}
                                      onChange={(e) => handleOvertimeChange(instructor.id, month, e.target.value)}
                                      style={{ width: "50px", margin: "0 auto", padding: "2px 4px", fontSize: "13px" }}
                                    />
                                  ) : (
                                    <span>{monthly[month] || 0}</span>
                                  )}
                                </td>
                              ))}
                              {/* 상반기 합계 */}
                              <td className="text-center" style={{ padding: "8px 2px", backgroundColor: "#f8f9fa" }}>
                                <strong>{firstHalfSum}</strong>
                              </td>
                              {/* 하반기 (7-12월) */}
                              {[7, 8, 9, 10, 11, 12].map((month) => (
                                <td key={month} className="text-center" style={{ padding: "8px 2px" }}>
                                  {isEditing ? (
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      value={monthly[month] || 0}
                                      onChange={(e) => handleOvertimeChange(instructor.id, month, e.target.value)}
                                      style={{ width: "50px", margin: "0 auto", padding: "2px 4px", fontSize: "13px" }}
                                    />
                                  ) : (
                                    <span>{monthly[month] || 0}</span>
                                  )}
                                </td>
                              ))}
                              {/* 하반기 합계 */}
                              <td className="text-center" style={{ padding: "8px 2px", backgroundColor: "#f8f9fa" }}>
                                <strong>{secondHalfSum}</strong>
                              </td>
                              <td className="text-center" style={{ padding: "8px 2px" }}>
                                {(() => {
                                  // 권한 체크: DIRECTOR가 아니고 자신의 데이터가 아닌 경우 액션 버튼 표시 안함
                                  const isDirector = userMeData?.instructor?.position === 'DIRECTOR';
                                  const isOwnData = instructor.id === userMeData?.instructor?.id;
                                  if (!isDirector && !isOwnData) return null;

                                  return isEditing ? (
                                    <div className="d-flex flex-column gap-1" style={{ alignItems: "center" }}>
                                      <Button
                                        type="button"
                                        variant="btn btn-sm btn-primary"
                                        onClick={() => handleSave(instructor.id)}
                                        style={{ fontSize: "11px", padding: "2px 8px" }}
                                      >
                                        저장
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="btn btn-sm btn-light"
                                        onClick={() => handleCancel(instructor.id)}
                                        style={{ fontSize: "11px", padding: "2px 8px" }}
                                      >
                                        취소
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      type="button"
                                      variant="btn btn-sm btn-outline-primary"
                                      onClick={() => handleEdit(instructor.id)}
                                      style={{ fontSize: "11px", padding: "2px 8px" }}
                                    >
                                      수정
                                    </Button>
                                  );
                                })()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
});

InstructorOvertime.displayName = "InstructorOvertime";
export default InstructorOvertime;

